import { useState, useCallback, useEffect, useRef } from 'react';
import { router, usePage } from '@inertiajs/react';
import SessionShell from '../shared/SessionShell';
import SessionCompleteSummary from '../shared/SessionCompleteSummary';
import ContextRecognition from '../shared/ContextRecognition';
import Stage3SynonymSort from './Stage3SynonymSort';
import Stage2_5Recognition from './Stage2_5Recognition';
import Stage4Cloze from './Stage4Cloze';
import TimedContinueButton from './TimedContinueButton';
import TeacherPassageActions from '../TeacherPassageActions';
import StudentPassageActions from '../StudentPassageActions';
import FlagQuestionButton from '../shared/FlagQuestionButton';
import useLiveActivitySync from '../../../hooks/useLiveActivitySync';
import { Pause } from 'lucide-react';

const STAGE_LABELS = {
  stage_1: 'Context',
  stage_2_5: 'Recognition',
  stage_3: 'Sorting',
  stage_4: 'Recall',
};

/**
 * The vocabulary onboarding flow body. Used by:
 *  - Solo onboarding page (`Student/Vocabulary/Onboarding/Show.jsx`) — interactive
 *  - Live-session student lobby — interactive
 *  - Live-session teacher show — `interactive={false}` (observer mode)
 *
 * Props mirror what `Vocabulary::OnboardingService.show_payload(session)` returns.
 *
 *  - interactive (bool, default true): when false, submit handlers no-op and
 *    the body is dimmed + has pointer-events disabled so the teacher can see
 *    the same stage UI without being able to advance the student's session.
 *  - onPause/onDone: callbacks for nav-out actions; hidden if not provided.
 *  - stepSignal ({ direction, ts }): increments when the teacher clicks
 *    Step forward / back on the controller. We listen on the student's
 *    interactive copy and either click Continue (if visible) or trigger the
 *    active form's Submit. `back` is currently a no-op (onboarding is
 *    forward-only server-side).
 *  - remoteState ({ id, current, cardIndex, showContinue, lastResult,
 *    complete, summary }): when interactive is false, the observer copy
 *    syncs its internal state from this prop on each `id` change so the
 *    teacher sees exactly what the student sees (current card, intermediate
 *    feedback, Continue button) without server round-trips.
 *  - onStateChange (state => void): when interactive is true, called with
 *    the latest internal state on each meaningful change so the parent can
 *    broadcast it to peers.
 */
export default function OnboardingActivity({
  session_id,
  batch_size,
  status,
  current: initialCurrent,
  session_data,
  // Reconstructed summary for already-completed sessions. The controller
  // re-derives it from session_data on revisit so the same per-word
  // recap + Sort breakdown the student saw at finalize time renders
  // again. Live submits still call setSummary from the response body.
  summary: initialSummary = null,
  // Click-to-navigate-back: when the student clicks a past segment in
  // the progress bar, the server re-renders this page with
  // readonly: true + past_result populated. Stage components are seeded
  // from past_result so they render in their resolved-state view; all
  // submit handlers are no-ops.
  readonly = false,
  past_result = null,
  onPause,
  onDone,
  showPause,
  interactive = true,
  stepSignal,
  remoteState,
  onStateChange,
  // Bidirectional sync directives. The student is the source of truth
  // for activity state; teacher drives shared affordances by emitting
  // a peer_intent over the cable. The student receives it via the
  // `peerIntent` prop here and applies to local state, which then
  // re-broadcasts via progress_state back to the teacher.
  //   - peerIntent     ({ action, payload, id }): set by the student
  //                    lobby when an incoming peer_intent arrives.
  //   - onPeerIntent   ((action, payload) => void): teacher's observer
  //                    view uses this to send commands toward the
  //                    student.
  peerIntent = null,
  onPeerIntent = null,
  // §3.15 — when set, this is the student's user_id and enables the
  // top-right Flag button for the teacher (observer mode). Posts to
  // /teacher/users/:user_id/vocabulary_flags. Unset on solo / student
  // surfaces; the student-side gating still applies for impersonators.
  flagAsTeacherFor = null,
  // Teacher's course lists, passed straight through to the passage
  // add-to-course-list menu so it needs no async fetch (the live observer's
  // frequent re-renders were disrupting the fetch-then-pick flow).
  courseListOptions = null,
}) {
  const page = usePage();
  const [current, setCurrent] = useState(initialCurrent);
  const [cardIndex, setCardIndex] = useState(
    // In readonly mode the server passes card_index on `current`; honor
    // it so the progress-bar highlight tracks the past step the student
    // is viewing rather than the session's real cursor.
    initialCurrent?.card_index ?? session_data?.current_card_index ?? 0
  );

  // Re-sync local state when the `initialCurrent` prop changes via an
  // Inertia partial reload — e.g. the teacher's step-back triggers
  // `router.reload({ data: { stage, card_index } })`, which returns a
  // readonly payload that needs to land in the active stage view.
  // Skipped on the very first mount: the initial useState above already
  // captured `initialCurrent`, and running this effect a second time
  // would noop-set the same value.
  const initialCurrentKey = `${initialCurrent?.card_id || ''}|${initialCurrent?.stage || ''}|${initialCurrent?.card_index ?? ''}`;
  const lastInitialCurrentKey = useRef(initialCurrentKey);
  useEffect(() => {
    if (initialCurrentKey === lastInitialCurrentKey.current) return;
    lastInitialCurrentKey.current = initialCurrentKey;
    if (!interactive) return;
    setCurrent(initialCurrent);
    setCardIndex(initialCurrent?.card_index ?? 0);
  }, [initialCurrentKey, interactive]);
  const [complete, setComplete] = useState(status === 'completed');
  const [summary, setSummary] = useState(initialSummary);
  const [showContinue, setShowContinue] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  // Timestamp the active auto-advance timer was scheduled. Drives the
  // Continue button's fill animation so the student sees the page is
  // about to advance. null when no timer is pending.
  const [autoAdvanceAt, setAutoAdvanceAt] = useState(null);
  // Per-stage transient input state lifted from the stage components so it
  // can ride along on `progress_state` broadcasts and reach the teacher's
  // observer mirror. Keyed by card_id so changing card resets it.
  const [stage1Text, setStage1Text] = useState('');
  const [stage1Attempts, setStage1Attempts] = useState([]);
  // Stage 1 example-hint controlled state. Lifted from
  // ExampleSentenceHint so it rides on progress_state broadcasts —
  // both peers see the same open/closed state and the same fetched
  // sentence when one was generated lazily. Teacher's observer view
  // can also toggle via peer_intent (see useLivePresenceChannel).
  const [stage1HintOpen, setStage1HintOpen] = useState(false);
  const [stage1FetchedExample, setStage1FetchedExample] = useState(null);
  const [stage2_5Selected, setStage2_5Selected] = useState(null);
  const [stage2_5Result, setStage2_5Result] = useState(null);
  const [stage4Text, setStage4Text] = useState('');
  const [stage4Result, setStage4Result] = useState(null);
  // Stage 4 collapsible hint panel (§3.6 POS + definition). Lifted
  // from Cloze.jsx for live-session bidirectional sync, same pattern
  // as the Stage 1 hint above.
  const [stage4HintOpen, setStage4HintOpen] = useState(false);
  // Stage 3 (synonym sort) state lifted for live-session bidir sync.
  // Each drop on either side re-broadcasts the full placement map so
  // the teacher's observer mirror tracks tile-by-tile movement. The
  // teacher can also drag tiles in the mirror: handleDragRequest emits
  // a peer_intent that the student applies + rebroadcasts.
  //   - stage3Assignments: { tileId: columnWordId | null }
  //   - stage3Unplaced:    [tileId] currently in the bank
  //   - stage3ColumnItems: { columnWordId: [tileId] }
  //   - stage3Discarded:   [tileId] in the "doesn't belong" zone
  //   - stage3ActiveId:    in-flight drag preview (null when no drag)
  //   - stage3Feedback:    grader response on submit
  const [stage3Assignments, setStage3Assignments] = useState({});
  const [stage3Unplaced, setStage3Unplaced] = useState([]);
  const [stage3ColumnItems, setStage3ColumnItems] = useState({});
  const [stage3Discarded, setStage3Discarded] = useState([]);
  const [stage3ActiveId, setStage3ActiveId] = useState(null);
  const [stage3Feedback, setStage3Feedback] = useState(null);
  const autoAdvanceTimer = useRef(null);

  // Reset per-card transient state when the active card changes (interactive
  // copy only — the observer mirror's state is dictated by remoteState).
  // In readonly mode (click-to-navigate-back) we seed from past_result
  // instead of resetting, so the past step renders in its resolved-state
  // view (their typed answer + the AI verdict / correct word / etc.).
  useEffect(() => {
    if (!interactive) return;
    if (readonly && past_result) {
      seedFromPastResult(current?.stage, past_result);
    } else {
      setStage1Text('');
      setStage1Attempts([]);
      setStage1HintOpen(false);
      setStage1FetchedExample(null);
      setStage2_5Selected(null);
      setStage2_5Result(null);
      setStage4Text('');
      setStage4Result(null);
      setStage4HintOpen(false);
      // Seed Stage 3 state from the batch's bank + columns when we land
      // on stage_3. Otherwise reset to empty so prior placements don't
      // bleed across sessions.
      if (current?.stage === 'stage_3' && current?.payload) {
        const bankIds = (current.payload.bank || []).map((b) => b.id);
        const cols = current.payload.columns || [];
        setStage3Assignments({});
        setStage3Unplaced(bankIds);
        setStage3ColumnItems(Object.fromEntries(cols.map((c) => [c.word_id, []])));
        setStage3Discarded([]);
        setStage3ActiveId(null);
        setStage3Feedback(null);
      } else {
        setStage3Assignments({});
        setStage3Unplaced([]);
        setStage3ColumnItems({});
        setStage3Discarded([]);
        setStage3ActiveId(null);
        setStage3Feedback(null);
      }
    }
  }, [interactive, current?.card_id, current?.stage, readonly, past_result]);

  function seedFromPastResult(stage, pr) {
    setStage1Text('');
    setStage1Attempts([]);
    setStage2_5Selected(null);
    setStage2_5Result(null);
    setStage4Text('');
    setStage4Result(null);
    if (!pr) return;

    if (stage === 'stage_1') {
      // Past attempts as rendered by ContextRecognition's `attempts`
      // controlled prop. Keep the original shape — { response, tier }.
      const attempts = Array.isArray(pr.attempts) ? pr.attempts : [];
      setStage1Attempts(
        attempts.map((a) => ({ response: a.response, tier: a.tier, explanation: a.explanation }))
      );
    } else if (stage === 'stage_2_5') {
      setStage2_5Selected(pr.choice_id ?? null);
      setStage2_5Result({
        correct: pr.correct === true,
        correctId: pr.correct_choice_id,
        selectedId: pr.choice_id,
      });
    } else if (stage === 'stage_4') {
      setStage4Text(pr.response ?? '');
      // Map cloze grader output (rating + kind) to the front-end tier
      // model that Cloze.jsx renders. See spec §3.9d for the kind values.
      const tierFromKind = (kind, rating) => {
        if (kind === 'exact') return rating === 'hard' ? 'hard_recovered' : 'good';
        if (kind === 'variant') return 'hard';
        if (kind === 'typo')    return 'hard_typo';
        return 'again';
      };
      setStage4Result({
        tier: tierFromKind(pr.kind, pr.rating),
        correctWord: pr.correct_answer || pr.accepted_surface_form,
        studentAnswer: pr.response,
        definition: null,
      });
    }
  }

  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  const csrfToken =
    typeof document !== 'undefined'
      ? document.querySelector('meta[name="csrf-token"]')?.content
      : '';

  const postSubmit = useCallback(
    async (body) => {
      if (!interactive) return { complete: false, next: null };
      const res = await fetch(
        `/student/vocabulary/onboarding_sessions/${session_id}/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        }
      );
      return res.json();
    },
    [session_id, csrfToken, interactive]
  );

  // Readonly mode (click-to-navigate-back from progress bar). Every submit
  // handler short-circuits so a student viewing a past step can't accidentally
  // re-answer it. Returns a no-op shape that won't trigger the result-handling
  // branches in the stage components.
  const READONLY_NOOP = { complete: false, next: null, readonly: true };

  const handleStage1Submit = async (text, { hint_used } = {}) => {
    if (readonly) return READONLY_NOOP;
    const data = await postSubmit({
      stage: 'stage_1',
      card_id: current.card_id,
      text,
      hint_used: hint_used === true,
    });
    setLastResult(data);
    setShowContinue(!!data?.next);
    return data;
  };

  // §3.24 — lazy fetch for the context-recognition example-hint when the
  // session-start cache only has the sentence already serving as primary
  // context. Returns the new example text (or null) for the hint UI.
  const handleStage1RequestExample = async () => {
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(
        `/student/vocabulary/onboarding_sessions/${session_id}/generate_example`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrf || '',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ card_id: current.card_id }),
        }
      );
      if (!res.ok) return null;
      const data = await res.json();
      return data?.sentence_text || null;
    } catch {
      return null;
    }
  };

  const handleStage2_5Submit = async (choiceId) => {
    if (readonly) return READONLY_NOOP;
    const data = await postSubmit({
      stage: 'stage_2_5',
      card_id: current.card_id,
      choice_id: choiceId,
    });
    setLastResult(data);
    setShowContinue(true);
    // Auto-advance disabled per user direction (UX-flow study window).
    // The student presses Continue to advance from MCQ feedback. Leaving
    // the timer scaffolding behind a comment so we can re-enable with a
    // different cadence later if needed.
    //
    // if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    // setAutoAdvanceAt(Date.now());
    // autoAdvanceTimer.current = setTimeout(() => {
    //   autoAdvanceTimer.current = null;
    //   setAutoAdvanceAt(null);
    //   if (data.complete) {
    //     setSummary(data.summary);
    //     setComplete(true);
    //   } else if (data.next) {
    //     setCurrent(data.next);
    //     setCardIndex(data.current_card_index ?? 0);
    //     setShowContinue(false);
    //     setLastResult(null);
    //   }
    // }, 2000);
    setAutoAdvanceAt(null);
    return data;
  };

  const handleStage3Submit = async (assignments) => {
    if (readonly) return READONLY_NOOP;
    const data = await postSubmit({
      stage: 'stage_3',
      assignments,
    });

    if (data.complete) {
      setSummary(data.summary);
      setComplete(true);
    } else if (data.next) {
      setCurrent(data.next);
      setCardIndex(data.current_card_index ?? 0);
      setShowContinue(false);
      setLastResult(null);
    }
    return data;
  };

  const handleStage4Submit = async (text, attempt = 1, opts = {}) => {
    if (readonly) return READONLY_NOOP;
    const data = await postSubmit({
      stage: 'stage_4',
      card_id: current.card_id,
      text,
      attempt,
      hint_used: opts.hint_used || false,
    });
    // Hint-and-retry on attempt-1-wrong: don't advance, don't record a
    // result. Cloze itself renders the expanded prefix + definition reveal.
    if (data?.retry) return data;

    setLastResult(data);
    setShowContinue(true);

    // Cloze auto-advance disabled per user direction — student presses
    // Continue to advance. Leaving the timer scaffolding in place behind
    // the comment so we can re-enable with a different cadence later.
    //
    // if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    // autoAdvanceTimer.current = setTimeout(() => {
    //   autoAdvanceTimer.current = null;
    //   if (data.complete) {
    //     setSummary(data.summary);
    //     setComplete(true);
    //   } else if (data.next) {
    //     setCurrent(data.next);
    //     setCardIndex(data.current_card_index ?? 0);
    //     setShowContinue(false);
    //     setLastResult(null);
    //   }
    // }, 2000);
    return data;
  };

  const handleContinue = () => {
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }
    setAutoAdvanceAt(null);
    if (lastResult?.complete) {
      setSummary(lastResult.summary);
      setComplete(true);
    } else if (lastResult?.next) {
      setCurrent(lastResult.next);
      setCardIndex(lastResult.current_card_index ?? 0);
      setShowContinue(false);
      setLastResult(null);
    }
  };

  // Teacher-driven Step back. Forward (Continue / click Submit), the
  // peer_intent / remoteState / broadcast / Enter plumbing all live in the
  // shared useLiveActivitySync hook below; this is the onboarding-specific
  // back body it injects as onStepBack. Onboarding is forward-only
  // server-side, so we re-render the past step read-only via the same route
  // the progress bar's click-to-navigate-back uses, rather than mutating
  // session state. (Review supplies no onStepBack → back is a no-op there.)
  const handleStepBack = () => {
    const stageOrder = stage3WasSkipped
      ? ['stage_1', 'stage_2_5', 'stage_4']
      : ['stage_1', 'stage_2_5', 'stage_3', 'stage_4'];
    const stageIdx = stageOrder.indexOf(current?.stage);
    if (stageIdx < 0) return;
    let prevStage = current?.stage;
    let prevCardIndex = cardIndex - 1;
    if (prevStage === 'stage_3' || prevCardIndex < 0) {
      // Stage 3 has a single unit; stepping back jumps to the last
      // card of the prior stage. Same fallthrough when we're at
      // card 0 of any per-card stage.
      if (stageIdx === 0) return; // already at the very start
      prevStage = stageOrder[stageIdx - 1];
      prevCardIndex = prevStage === 'stage_3' ? 0 : batch_size - 1;
    }
    // Use a partial reload so the student stays on the same URL
    // (live lobby vs. solo onboarding). Both controllers honor
    // ?stage=&card_index= and return a readonly activity payload.
    router.reload({
      data: { stage: prevStage, card_index: prevCardIndex },
      only: ['activity'],
      preserveScroll: true,
    });
  };

  // Sectioned progress model — one section per onboarding stage. Each
  // per-card stage gets `batch_size` unit-cells; Stage 3 (Synonym sort)
  // is a single unit because the whole batch is sorted at once.
  // The active section's current cell is the one the student is on now;
  // sections to the left are fully filled, to the right are untouched.
  //
  // B5: Stage 3 is conditionally skipped by OnboardingService when the
  // batch doesn't have enough synonyms to build a sort. When skipped,
  // hide the Sorting cell entirely — showing it greyed-out is misleading.
  // We can't rely on `session_data.stage_3_sort` alone — it's captured
  // at page-load and stays stale after the student advances. Instead,
  // remember whether we ever rendered stage_3 during this page session;
  // seed the flag from session_data so a reload mid-Stage 4 still
  // resolves correctly.
  const [stage3WasSeen, setStage3WasSeen] = useState(
    initialCurrent?.stage === 'stage_3' || !!session_data?.stage_3_sort
  );
  useEffect(() => {
    if (current?.stage === 'stage_3') setStage3WasSeen(true);
  }, [current?.stage]);
  const stage3WasSkipped = current?.stage === 'stage_4' && !stage3WasSeen;

  const getProgressSections = () => {
    const stage = current?.stage;
    const stageOrder = stage3WasSkipped
      ? ['stage_1', 'stage_2_5', 'stage_4']
      : ['stage_1', 'stage_2_5', 'stage_3', 'stage_4'];
    const stageIdx = stageOrder.indexOf(stage);

    const completedFor = (sectionStage) => {
      const idx = stageOrder.indexOf(sectionStage);
      if (stageIdx < 0) return 0;
      if (idx < stageIdx) {
        return sectionStage === 'stage_3' ? 1 : batch_size;
      }
      if (idx > stageIdx) return 0;
      return sectionStage === 'stage_3' ? 0 : cardIndex;
    };

    // `stage` is included so the click-to-navigate-back handler knows
    // which stage to route to on each cell click.
    const sections = [
      { label: 'Context',     stage: 'stage_1',   units: batch_size, completed: completedFor('stage_1'),   active: stage === 'stage_1' },
      { label: 'Recognition', stage: 'stage_2_5', units: batch_size, completed: completedFor('stage_2_5'), active: stage === 'stage_2_5' },
    ];
    if (!stage3WasSkipped) {
      sections.push({ label: 'Sorting', stage: 'stage_3', units: 1, completed: completedFor('stage_3'), active: stage === 'stage_3' });
    }
    sections.push({ label: 'Recall', stage: 'stage_4', units: batch_size, completed: completedFor('stage_4'), active: stage === 'stage_4' });
    return sections;
  };

  // Click-to-navigate-back on the progress bar. Past + current cells are
  // clickable (the ProgressBar gates "future" out). We round-trip through
  // the server so the controller can rebuild the past stage's payload via
  // OnboardingService.build_stage_payload AND seed the resolved-state
  // view of what the student already answered.
  const handleSegmentClick = ({ stage: clickedStage, unitIndex }) => {
    if (!interactive) return;
    // Clicking the current cell is a no-op (we're already there).
    if (clickedStage === current?.stage && unitIndex === cardIndex) return;
    router.visit(
      `/student/vocabulary/onboarding_sessions/${session_id}?stage=${clickedStage}&card_index=${unitIndex}`
    );
  };

  // Live-session sync (peer_intent, remoteState pull, progress_state
  // broadcast, stepSignal, Enter-to-Continue, observer lock) lives in the
  // shared hook. This component injects only its own field set, the broadcast
  // deps, and the onboarding-specific step-back body.
  // NOTE: `broadcastState` and `broadcastDeps` must stay in lock-step — the
  // exhaustive-deps lint can't see a deps array passed as a variable.
  const { fieldsetProps } = useLiveActivitySync({
    interactive,
    readonly,
    remoteState,
    applyRemoteState: (rs) => {
      if (rs.current !== undefined) setCurrent(rs.current);
      if (rs.cardIndex !== undefined) setCardIndex(rs.cardIndex);
      if (rs.showContinue !== undefined) setShowContinue(rs.showContinue);
      if (rs.lastResult !== undefined) setLastResult(rs.lastResult);
      if (rs.complete !== undefined) setComplete(rs.complete);
      if (rs.summary !== undefined) setSummary(rs.summary);
      if (rs.stage1Text !== undefined) setStage1Text(rs.stage1Text);
      if (rs.stage1Attempts !== undefined) setStage1Attempts(rs.stage1Attempts);
      if (rs.stage1HintOpen !== undefined) setStage1HintOpen(rs.stage1HintOpen);
      if (rs.stage1FetchedExample !== undefined) setStage1FetchedExample(rs.stage1FetchedExample);
      if (rs.stage2_5Selected !== undefined) setStage2_5Selected(rs.stage2_5Selected);
      if (rs.stage2_5Result !== undefined) setStage2_5Result(rs.stage2_5Result);
      if (rs.stage4Text !== undefined) setStage4Text(rs.stage4Text);
      if (rs.stage4Result !== undefined) setStage4Result(rs.stage4Result);
      if (rs.stage4HintOpen !== undefined) setStage4HintOpen(rs.stage4HintOpen);
      if (rs.stage3Assignments !== undefined) setStage3Assignments(rs.stage3Assignments);
      if (rs.stage3Unplaced !== undefined) setStage3Unplaced(rs.stage3Unplaced);
      if (rs.stage3ColumnItems !== undefined) setStage3ColumnItems(rs.stage3ColumnItems);
      if (rs.stage3Discarded !== undefined) setStage3Discarded(rs.stage3Discarded);
      if (rs.stage3Feedback !== undefined) setStage3Feedback(rs.stage3Feedback);
      // stage3ActiveId is intentionally NOT synced — each peer keeps a
      // local drag preview so a teacher's in-flight drag isn't blanked
      // by an unrelated student-side state broadcast and vice versa.
    },
    peerIntent,
    applyPeerIntent: (action, payload) => {
      if (action === 'set_stage1_hint') {
        if (payload?.open !== undefined) setStage1HintOpen(!!payload.open);
        if (payload?.fetched_example !== undefined) {
          setStage1FetchedExample(payload.fetched_example);
        }
      } else if (action === 'set_stage4_hint') {
        if (payload?.open !== undefined) setStage4HintOpen(!!payload.open);
      } else if (action === 'apply_stage3_move') {
        // Teacher dragged a tile in their mirror; payload carries the
        // computed next-state shape from Stage3SynonymSort.computeMove.
        if (payload?.assignments !== undefined) setStage3Assignments(payload.assignments);
        if (payload?.unplaced !== undefined) setStage3Unplaced(payload.unplaced);
        if (payload?.columnItems !== undefined) setStage3ColumnItems(payload.columnItems);
        if (payload?.discarded !== undefined) setStage3Discarded(payload.discarded);
      }
    },
    onStateChange,
    broadcastState: {
      current,
      cardIndex,
      showContinue,
      lastResult,
      complete,
      summary,
      stage1Text,
      stage1Attempts,
      stage1HintOpen,
      stage1FetchedExample,
      stage2_5Selected,
      stage2_5Result,
      stage4Text,
      stage4Result,
      stage4HintOpen,
      stage3Assignments,
      stage3Unplaced,
      stage3ColumnItems,
      stage3Discarded,
      stage3Feedback,
    },
    broadcastDeps: [
      interactive,
      current?.card_id,
      current?.stage,
      cardIndex,
      showContinue,
      lastResult?.tier,
      lastResult?.next?.card_id,
      complete,
      summary,
      stage1Text,
      stage1Attempts,
      stage1HintOpen,
      stage1FetchedExample,
      stage2_5Selected,
      stage2_5Result?.selectedId,
      stage2_5Result?.correct,
      stage4Text,
      stage4Result?.tier,
      stage4Result?.correctWord,
      stage4Result?.studentAnswer,
      stage4HintOpen,
      stage3Assignments,
      stage3Unplaced,
      stage3ColumnItems,
      stage3Discarded,
      stage3Feedback,
    ],
    stepSignal,
    showContinue,
    onContinue: handleContinue,
    onStepBack: handleStepBack,
    lastResult,
  });

  if (complete) {
    return (
      <SessionShell topLabel="Onboarding complete">
        <SessionCompleteSummary
          wordsCount={summary?.words_onboarded || batch_size}
          firstAttemptRate={summary?.first_attempt_rate}
          stage3Correct={summary?.stage_3_correct}
          stage3Total={summary?.stage_3_total}
          stage3Skipped={summary?.stage_3_skipped}
          stage3TileResults={summary?.stage_3_tile_results}
          words={summary?.words}
          onDone={onDone}
          sessionType="onboarding"
        />
      </SessionShell>
    );
  }

  if (!current) {
    return (
      <SessionShell topLabel="Loading...">
        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
          Loading session...
        </div>
      </SessionShell>
    );
  }

  const stageLabel = `${STAGE_LABELS[current.stage] || current.stage}`;
  const progressSections = getProgressSections();
  const shouldShowPause = (showPause ?? true) && typeof onPause === 'function';

  // Flag-question affordance — visible to:
  //   - the student-side interactive flow for impersonators (admins
  //     walking through a student's session) and admins generally;
  //   - the teacher-side observer flow during a Live Session, where the
  //     parent (Teacher/LiveSessions/Show.jsx) passes flagAsTeacherFor
  //     with the student's user_id so the teacher can flag any item
  //     they spot while watching.
  // Real students still don't see this yet — we capture flags from
  // walkthroughs first to feed the repair queue.
  const auth = page.props?.auth;
  const canFlagAsStudentSide = interactive && !readonly && Boolean(
    auth?.impersonating?.active || auth?.user?.role === 'admin'
  );
  const canFlagAsTeacher = !readonly && Boolean(
    flagAsTeacherFor != null && auth?.user?.role === 'teacher'
  );
  const canFlag = canFlagAsStudentSide || canFlagAsTeacher;
  const flagEndpoint = canFlagAsTeacher
    ? `/teacher/users/${flagAsTeacherFor}/vocabulary_flags`
    : '/student/vocabulary/flags';

  return (
    <SessionShell
      // Sectioned bar carries its own per-stage labels in interactive mode,
      // so the top-of-shell label is suppressed there. Observer mode keeps
      // the "Watching · X" prefix because the teacher needs to see which
      // stage the student is on at a glance even when not driving it.
      topLabel={interactive ? null : `Watching · ${stageLabel}`}
      progressSections={progressSections}
      // Click-to-navigate-back from the sectioned progress bar. Only
      // wired in interactive mode; observer view is non-navigable.
      onSegmentClick={interactive ? handleSegmentClick : undefined}
      topRight={
        <div className="flex items-center gap-3">
          <FlagQuestionButton
            enabled={canFlag}
            endpoint={flagEndpoint}
            payload={{
              user_word_card_id: current?.card_id,
              vocabulary_session_id: session_id,
              stage_or_format: current?.stage,
            }}
          />
          {shouldShowPause && (
            <button
              onClick={onPause}
              className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1 transition-colors"
            >
              <Pause className="w-3.5 h-3.5" />
              Pause
            </button>
          )}
        </div>
      }
    >
      {/* Click-to-navigate-back banner: the student is viewing a past
          step in read-only mode. Single click returns to the live cursor. */}
      {readonly && (
        <div className="mb-6 px-4 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            Looking back at this step — read-only.
          </span>
          <button
            type="button"
            onClick={() => router.visit(`/student/vocabulary/onboarding_sessions/${session_id}`)}
            className="text-gray-700 dark:text-gray-200 font-medium hover:underline"
          >
            ← Back to current step
          </button>
        </div>
      )}

      {/* Observer (teacher) lock — computed in useLiveActivitySync. The live
          observer (!interactive) uses pointer-events-none so the clue / example
          toggle can opt back in via .pointer-events-auto and sync via
          peer_intent. A readonly past-stage view stays fully `disabled`. */}
      <fieldset {...fieldsetProps}>
      {current.stage === 'stage_1' && (() => {
        const stage1 = (
          <ContextRecognition
            key={`stage_1-${current.card_id}`}
            lemma={current.payload.lemma}
            contextSentence={current.payload.context_sentence}
            contextBookTitle={current.payload.context_book_title}
            contextChapterTitle={current.payload.context_chapter_title}
            additionalExamples={current.payload.additional_examples}
            prompt={current.payload.prompt}
            chapterContext={current.payload.chapter_context}
            onSubmit={handleStage1Submit}
            // Only the student (interactive) calls the generate-example
            // endpoint — the teacher's observer view just waits for the
            // student's fetched_example to broadcast in via remoteState.
            onRequestExample={interactive ? handleStage1RequestExample : undefined}
            text={stage1Text}
            attempts={stage1Attempts}
            onTextChange={setStage1Text}
            onAttempt={(attempt, { replace }) =>
              setStage1Attempts((prev) => (replace ? [attempt] : [...prev, attempt]))
            }
            hintOpen={stage1HintOpen}
            hintFetchedExample={stage1FetchedExample}
            onHintOpenChange={(open) => {
              // Teacher observer: drive the student via peer_intent so
              // the student becomes the source of truth. Student: just
              // toggle local state; the broadcast happens via the
              // existing progress_state effect.
              if (!interactive) {
                if (typeof onPeerIntent === 'function') {
                  onPeerIntent('set_stage1_hint', { open });
                }
              } else {
                setStage1HintOpen(open);
              }
            }}
            onHintFetchedExampleChange={(text) => {
              if (!interactive) {
                if (typeof onPeerIntent === 'function') {
                  onPeerIntent('set_stage1_hint', { fetched_example: text });
                }
              } else {
                setStage1FetchedExample(text);
              }
            }}
          />
        );
        const chapterId = current.payload.chapter_context?.chapter_id;
        // §3.4 — observer (teacher) mode wraps in TeacherPassageActions for
        // word-add + send-to-Sentence-Studio. Always mount it so add-to-course
        // works regardless of whether the card has a chapter source; course
        // lists are passed in so the menu needs no async fetch. Interactive
        // (student) mode wraps in StudentPassageActions for personal-list adds
        // (still gated on a chapter source for now).
        if (!interactive) {
          return (
            <TeacherPassageActions chapterId={chapterId} courseListOptions={courseListOptions}>
              {stage1}
            </TeacherPassageActions>
          );
        }
        if (!chapterId) return stage1;
        return <StudentPassageActions chapterId={chapterId}>{stage1}</StudentPassageActions>;
      })()}

      {current.stage === 'stage_2_5' && (
        <Stage2_5Recognition
          key={`stage_2_5-${current.card_id}`}
          lemma={current.payload.lemma}
          contextHint={current.payload.context_hint}
          options={current.payload.options}
          onSubmit={handleStage2_5Submit}
          selected={stage2_5Selected}
          result={stage2_5Result}
          onSelect={setStage2_5Selected}
          onResult={setStage2_5Result}
        />
      )}

      {current.stage === 'stage_3' && (
        <Stage3SynonymSort
          key={`stage_3-${current.card_id}`}
          columns={current.payload.columns}
          bank={current.payload.bank}
          onSubmit={handleStage3Submit}
          assignments={stage3Assignments}
          unplaced={stage3Unplaced}
          columnItems={stage3ColumnItems}
          discarded={stage3Discarded}
          activeId={stage3ActiveId}
          feedback={stage3Feedback}
          onAssignmentsChange={setStage3Assignments}
          onUnplacedChange={setStage3Unplaced}
          onColumnItemsChange={setStage3ColumnItems}
          onDiscardedChange={setStage3Discarded}
          onActiveIdChange={setStage3ActiveId}
          onFeedbackChange={setStage3Feedback}
          onDragRequest={
            interactive
              ? undefined
              : (move) => {
                  // Teacher's observer-side drag: hand the computed
                  // next-state to the student via peer_intent. The
                  // student applies + rebroadcasts via progress_state.
                  if (typeof onPeerIntent === 'function') {
                    onPeerIntent('apply_stage3_move', {
                      assignments: move.assignments,
                      unplaced: move.unplaced,
                      columnItems: move.columnItems,
                      discarded: move.discarded,
                    });
                  }
                }
          }
        />
      )}

      {current.stage === 'stage_4' && (
        <Stage4Cloze
          key={`stage_4-${current.card_id}`}
          lemma={current.payload.lemma}
          sentenceWithBlank={current.payload.sentence_with_blank}
          firstLetter={current.payload.first_letter}
          remainingLength={current.payload.remaining_length}
          maskPattern={current.payload.mask_pattern}
          sentenceHtml={current.payload.sentence_html}
          acceptedSurfaceForm={current.payload.accepted_surface_form}
          definition={current.payload.definition}
          pos={current.payload.pos}
          skip={current.payload.skip}
          debugReason={current.payload.debug_reason}
          debugContext={current.payload.debug_context}
          onSubmit={handleStage4Submit}
          text={stage4Text}
          result={stage4Result}
          onTextChange={setStage4Text}
          onResult={setStage4Result}
          hintOpen={stage4HintOpen}
          onHintOpenChange={(open) => {
            if (!interactive) {
              if (typeof onPeerIntent === 'function') {
                onPeerIntent('set_stage4_hint', { open });
              }
            } else {
              setStage4HintOpen(open);
            }
          }}
        />
      )}

      {showContinue && current.stage !== 'stage_3' && (
        <div className="mt-8 flex justify-center">
          <TimedContinueButton
            autoAdvanceAt={autoAdvanceAt}
            duration={2000}
            onClick={handleContinue}
          />
        </div>
      )}
      </fieldset>
    </SessionShell>
  );
}
